import React, { useState, useEffect } from "react";
import axios from "axios";
import API from "../API";
import reportsAPI from "../API/reports";
import { isoDateToHumanReadableDate } from "../Util";
export const useEquipmentsMobilization = (id, startDate, endDate, page = 1, perPage = 15) => {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(true);
      return;
    }

    const fetchEquipments = async () => {
      try {
        setLoading(true);
        const response = await API.getEquipmentsMobilization(
          id,
          startDate,
          endDate,
          page,
          perPage
        );
        
        let formattedEquipments;
        if (response.pagination) {
          formattedEquipments = response.data.map((equipment) => ({
            equipmentName: equipment.equipment_name,
            quantityAsPerContract: equipment.quantity_and_type_as_per_contract,
            quantityMobilized: equipment.quantity_mobilized,
            statusOfEquipment: equipment.status_of_equipment,
            totalAvailableNow: equipment.total_available_now,
            remarks: equipment.remarks,
          }));
          
          setEquipments(formattedEquipments);
          setPagination(response.pagination);
        } else {
          formattedEquipments = response.data.map((equipment) => ({
            equipmentName: equipment.equipment_name,
            quantityAsPerContract: equipment.quantity_and_type_as_per_contract,
            quantityMobilized: equipment.mobilized,
            statusOfEquipment: equipment.status_of_equipment,
            totalAvailableNow: equipment.total_available_now,
            remarks: equipment.remarks,
          }));
          
          setEquipments(formattedEquipments);
          setPagination({
            current_page: 1,
            per_page: response.data.length,
            total: response.data.length,
            last_page: 1,
            from: 1,
            to: response.data.length
          });
        }

      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEquipments();
  }, [id, startDate, endDate, page, perPage]);

  return { equipments, loading, error, pagination };
};
export const useChallenges = (id, startDate, endDate) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageChallenges(id, startDate, endDate);
        setChallenges(response);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, [id, startDate, endDate]);

  return { challenges, loading, error };
};

export const usePctChallenges = (
  id = null,
  startDate = null,
  endDate = null
) => {
  const [pctChallenges, setPctChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPctChallenges = async () => {
      try {
        setLoading(true);
        const response = await API.getPctChallenges(id, startDate, endDate);
        console.log("PCT Challenges Response::", response);

        if (response?.data?.data && Array.isArray(response.data.data)) {
          const formattedData = response.data.data.map((item) => ({
            ...item,
            id: item.id,
            pct_report_id: item.pct_report_id,
            challenge_description: item.challenge_description,
            action_taken: item.action_taken,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            email: item.user?.email,
            staff_name: item.pct_report?.staff_name,
            section: item.pct_report?.section,
            way_forward: item.way_forward,
          }));
          setPctChallenges(formattedData);
        } else {
          console.log("PCT Challenges data is not an array or is empty:", response?.data?.data);
          setPctChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch PCT challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPctChallenges();
  }, [id, startDate, endDate]);

  return { pctChallenges, loading, error };
};
export const useWorkSummary = (id, startDate, endDate) => {
  const [workSummary, setWorkSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setWorkSummary({});
      setError(null);
      return;
    }

    const fetchWorkSummary = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageContractProgress(
          id,
          startDate,
          endDate
        );
        const data = response[0];
        setWorkSummary(data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkSummary();
  }, [id, startDate, endDate]);

  return { workSummary, loading, error };
};
export const useGrievances = (id, page = 1, perPage = 10) => {
  const [grievances, setGrievances] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGrievances = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageGrievances(id, null, null, page, perPage);


        if (response.success && response.data) {
          const formattedGrievances = response.data.map((grievance) => ({
            ...grievance,
            Sub_project_name: grievance.sub_project?.name || "",
            GrievanceCategory: grievance.grievance_category
              .map((category) => category.name)
              .join(", "),
            GrievanceDescription: grievance.grievance_description || "",
            Summary_of_the_Steps_Taken: grievance.summary_of_steps_taken || "",
            GrievanceStatus:
              grievance.Grievance_Status?.filter(
                (status) => status.count > 0
              ).map((status) => status.name) || [],
            GrievanceReceived: grievance.total_grievances || 0,
            construction_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "construction_related"
              )?.count || 0,
            compensation_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "compensation_related"
              )?.count || 0,
            gender_based_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "gender_based_related"
              )?.count || 0,
            labour_based_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "labour_based_related"
              )?.count || 0,

            resolved:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "resolved"
              )?.count || 0,
            resolved_document_url:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "resolved"
              )?.document || "",
            pending:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "pending"
              )?.count || 0,
            in_progress:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "in_progress"
              )?.count || 0,

            report_start_date: grievance.report_start_date
              ? new Date(grievance.report_start_date).toLocaleDateString("en-GB")
              : null,
            report_end_date: grievance.report_end_date
              ? new Date(grievance.report_end_date).toLocaleDateString("en-GB")
              : null,
          }));
          setGrievances(formattedGrievances);
          setPagination(response.pagination);
        } else {

          const formattedGrievances = response.map((grievance) => ({
            ...grievance,
            Sub_project_name: grievance.sub_project?.name || "",
            GrievanceCategory: grievance.grievance_category
              .map((category) => category.name)
              .join(", "),
            GrievanceDescription: grievance.grievance_description || "",
            Summary_of_the_Steps_Taken: grievance.summary_of_steps_taken || "",
            GrievanceStatus:
              grievance.Grievance_Status?.filter(
                (status) => status.count > 0
              ).map((status) => status.name) || [],
            GrievanceReceived: grievance.total_grievances || 0,
            construction_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "construction_related"
              )?.count || 0,
            compensation_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "compensation_related"
              )?.count || 0,
            gender_based_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "gender_based_related"
              )?.count || 0,
            labour_based_related:
              grievance.grievance_category?.find(
                (c) => c.name.toLowerCase() === "labour_based_related"
              )?.count || 0,

            resolved:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "resolved"
              )?.count || 0,
            resolved_document_url:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "resolved"
              )?.document || "",
            pending:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "pending"
              )?.count || 0,
            in_progress:
              grievance.grievance_status?.find(
                (status) => status.name.toLowerCase() === "in_progress"
              )?.count || 0,

            report_start_date: grievance.report_start_date
              ? new Date(grievance.report_start_date).toLocaleDateString("en-GB")
              : null,
            report_end_date: grievance.report_end_date
              ? new Date(grievance.report_end_date).toLocaleDateString("en-GB")
              : null,
          }));
          setGrievances(formattedGrievances);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGrievances();
  }, [id, page, perPage]);

  return { grievances, pagination, loading, error };
};
export const useFinancialStatuses = (id, startDate, endDate, page = 1, perPage = 10) => {
  const [financialStatuses, setFinancialStatuses] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
        if (!id) {
      setLoading(true);
      return;
    }
    const fetchFinancialStatuses = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageFinancialStatus(
          id,
          startDate,
          endDate,
          page,
          perPage
        );
        console.log("Financial Statuses Response::", response);

        if (response.success && response.data) {
          const formattedStatuses = response.data.map((status) => ({
            id: status.id,
            invoiceNo: status.invoice_no,
            ipcNo: status.ipc_no,
            particulars: status.particulars,
            certifiedAmount: status.certified_amount,
            paidAmount: status.paid_amount,
            paid_date: status.paid_date,
            ipc_no_description: status.ipc_no_description,
            unpaid_currency: status.unpaid_currency,
            remaining_package_balance: status.remaining_package_balance,
            status: status.status,
            remarks: status.remarks,
            financial_progress: status.financial_progress,
            delayed_days: status.delayed_days,
            submissionDate: status.submission_date
              ? new Date(status.submission_date).toLocaleDateString()
              : null,
            paymentDueDate: status.payment_due_date
              ? new Date(status.payment_due_date).toLocaleDateString()
              : null,
            interest: status.interest_calculation_closing_date,
            reportStartDate: status.report_start_date
              ? new Date(status.report_start_date).toLocaleDateString()
              : null,
            reportEndDate: status.report_end_date
              ? new Date(status.report_end_date).toLocaleDateString()
              : null,
            procuringEntityPackageContractId:
              status.procuring_entity_package_contract_id,
          }));

          setFinancialStatuses(formattedStatuses);
          setPagination(response.pagination);
        } else {
          // Handle legacy response format
          const legacyData = response.data || response || [];
          const formattedStatuses = legacyData.map((status) => ({
            id: status.id,
            invoiceNo: status.invoice_no,
            ipcNo: status.ipc_no,
            particulars: status.particulars,
            certifiedAmount: status.certified_amount,
            paidAmount: status.paid_amount,
            paid_date: status.paid_date,
            ipc_no_description: status.ipc_no_description,
            unpaid_currency: status.unpaid_currency,
            remaining_package_balance: status.remaining_package_balance,
            status: status.status,
            remarks: status.remarks,
            financial_progress: status.financial_progress,
            delayed_days: status.delayed_days,
            submissionDate: status.submission_date
              ? new Date(status.submission_date).toLocaleDateString()
              : null,
            paymentDueDate: status.payment_due_date
              ? new Date(status.payment_due_date).toLocaleDateString()
              : null,
            interest: status.interest_calculation_closing_date,
            reportStartDate: status.report_start_date
              ? new Date(status.report_start_date).toLocaleDateString()
              : null,
            reportEndDate: status.report_end_date
              ? new Date(status.report_end_date).toLocaleDateString()
              : null,
            procuringEntityPackageContractId:
              status.procuring_entity_package_contract_id,
          }));

          setFinancialStatuses(formattedStatuses);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch financial statuses data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialStatuses();
  }, [id, startDate, endDate, page, perPage]);

  return { financialStatuses, pagination, loading, error };
};

export const useEnvironmentalSocialSafeguards = (id, startDate, endDate, page = 1, perPage = 15) => {
  const [environmentalSafeguards, setEnvironmentalSafeguards] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEnvironmentalSafeguards = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageEnvironmentalSocialSafeguards(
          id,
          startDate,
          endDate,
          page,
          perPage
        );
        console.log("EnvSocialSafeGuards::>>", response);

        const data = response.data || response;
        const paginationData = response.pagination || {
          current_page: page,
          per_page: perPage,
          total: data.length,
          last_page: Math.ceil(data.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, data.length),
          has_more_pages: page < Math.ceil(data.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        const formattedSafeguards = data.map((safeguard) => ({
          ...safeguard,
          subProjectName: safeguard.sub_project.name,
          compliance_ess: safeguard.compliance_ess,
          compliance_esmp: Object.keys(safeguard.esmp_compliance)
            .filter((key) => safeguard.esmp_compliance[key])
            .filter((item) => item.toLowerCase() !== "other")
            .join(", "),
          non_compliance_esmp: Object.keys(safeguard.esmp_compliance)
            .filter((key) => !safeguard.esmp_compliance[key])
            .filter((item) => item.toLowerCase() !== "other")
            .join(", "),
          remarks: safeguard.remarks,
        }));

        setEnvironmentalSafeguards(formattedSafeguards);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEnvironmentalSafeguards();
  }, [id, startDate, endDate, page, perPage]);

  return { environmentalSafeguards, pagination, loading, error };
};
export const useStaffDetails = (id, startDate, endDate, page = 1, pageSize = 15) => {
  const [staffDetails, setStaffDetails] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      // If no id is provided yet (e.g. parent data still loading), do not
      // attempt to fetch — avoid showing a brief loading -> "no data" -> data
      // flash in the UI. Keep empty results until a valid id is available.
      setLoading(true);
      return;
    }
    const fetchStaffDetails = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageContractStaffs(
          id,
          startDate,
          endDate,
          page,
          pageSize
        );

        if (response && response.data && response.pagination) {
          setStaffDetails(response.data);
          setPagination(response.pagination);
        } else {
          setStaffDetails(response || []);
          setPagination({
            current_page: 1,
            per_page: response?.length || 0,
            total: response?.length || 0,
            last_page: 1,
            from: 1,
            to: response?.length || 0,
            has_more_pages: false,
            next_page_url: null,
            prev_page_url: null
          });
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStaffDetails();
  }, [id, startDate, endDate, page, pageSize]);

  return { staffDetails, pagination, loading, error };
};
// Hook for fetching and formatting problems constraints data
export const useProblemsConstraints = (id, startDate, endDate) => {
  const [problemsConstraints, setProblemsConstraints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProblemsConstraints = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageProblemsConstrains(
          id,
          startDate,
          endDate
        );
        console.log("PROBLEMS::", response);

        const formattedData = response.map((data) => ({
          title: data.title,
          descriptions: data.descriptions,
          issue_management_activities: data.issue_management_activities,
          status: data.status,
          recomendation: data.recomendation,
        }));

        setProblemsConstraints(formattedData);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProblemsConstraints();
  }, [id, startDate, endDate]);

  return { problemsConstraints, loading, error };
};

export const useIncidents = (id, startDate, endDate, page = 1, perPage = 10) => {
  const [incidents, setIncidents] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageIncidents(id, startDate, endDate, page, perPage);
        
        if (response.success && response.data) {
          setIncidents(response.data);
          setPagination(response.pagination);
        } else {
          setIncidents(response);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidents();
  }, [id, startDate, endDate, page, perPage]);

  return { incidents, pagination, loading, error };
};
export const useRelocationUtilities = (id, startDate, endDate, page = 1, perPage = 10) => {
  const [relocationUtilities, setRelocationUtilities] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRelocationUtilities = async () => {
      try {
        setLoading(true);
        const response = await API.getPackageRelocationUtilities(
          id,
          startDate,
          endDate,
          page,
          perPage
        );

        if (response.success && response.data) {
          const formattedData = response.data.map((data) => ({
            id: data.id,
            sub_project_name: data.sub_project_name,
            Description: data.description,
            chainage_of_occurrence: data.chainage_of_occurrence,
            Responsible_Parties: data.responsible_parties,
            status: data.status,
            Remarks: data.remarks,
            subProjectName: data.sub_project_name,
            package_number: data.package_number,
          }));

          setRelocationUtilities(formattedData);
          setPagination(response.pagination);
        } else {
          // Handle legacy response format
          const legacyData = response || [];
          const formattedData = legacyData.map((data) => ({
            id: data.id,
            sub_project_name: data.sub_project_name,
            Description: data.description,
            chainage_of_occurrence: data.chainage_of_occurrence,
            Responsible_Parties: data.responsible_parties,
            status: data.status,
            Remarks: data.remarks,
            subProjectName: data.sub_project_name,
            package_number: data.package_number,
          }));

          setRelocationUtilities(formattedData);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch relocation utilities data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRelocationUtilities();
  }, [id, startDate, endDate, page, perPage]);

  return { relocationUtilities, pagination, loading, error };
};

export const useMaterialTestingApprovals = (id, startDate, endDate) => {
  const [socialSafeguards, setSocialSafeguards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaterialTestingApprovals = async () => {
      try {
        setLoading(true);
        const response = await API.getMaterialTestingApprovals(
          id,
          startDate,
          endDate
        );

        const materials = response?.data || response || [];
        
        const formattedMaterials = materials.map((material) => ({
          id: material.id,
          package_id: material.package_id,
          sub_project_id: material.sub_project_id,
          sub_project: material.sub_project?.name || "-",
          material_description: material.material_description || "-",
          material_source: material.material_source || "-",
          material_quantity_mobilized: material.material_quantity_mobilized || 0,
          material_mobilization_status: material.material_mobilization_status || 0,
          remarks: material.remarks || "-",
          report_start_date: material.report_start_date || "-",
          report_end_date: material.report_end_date || "-",
          created_at: material.created_at,
          updated_at: material.updated_at,
        }));

        setSocialSafeguards(formattedMaterials);
      } catch (err) {
        console.error("Failed to fetch material testing approvals:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMaterialTestingApprovals();
    } else {
      setLoading(false);
    }
  }, [id, startDate, endDate]);

  return { socialSafeguards, loading, error };
};

export const useConsultantFinancialStatus = (id, startDate, endDate, page = 1, perPage = 15) => {
  const [consultantFinancial, setConsultantFinancial] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConsultantFinancial = async () => {
      try {
        setLoading(true);
        const response = await API.getConsultantFinancialStatus(
          id,
          startDate,
          endDate,
          page,
          perPage
        );

        // Handle paginated response structure
        const data = response.data || response;
        const paginationData = response.pagination || {
          current_page: page,
          per_page: perPage,
          total: data.length,
          last_page: Math.ceil(data.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, data.length),
          has_more_pages: page < Math.ceil(data.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        const formattedFinancial = data.map((finance) => ({
          ...finance,
          invoice_no: finance.invoice_no,
          description: finance.description,
          remarks: finance.remarks,
        }));

        setConsultantFinancial(formattedFinancial);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsultantFinancial();
  }, [id, startDate, endDate, page, perPage]);

  return { consultantFinancial, pagination, loading, error };
};

export const useCscStaffs = (contractId, page = 1, perPage = 15) => {
  const [cscStaffs, setCscStaffs] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCscStaffs = async () => {
      try {
        setLoading(true);
        const response = await API.getCscStaffs(contractId, page, perPage);

        // Handle paginated response structure
        const data = response.data || response;
        const paginationData = response.pagination || {
          current_page: page,
          per_page: perPage,
          total: data.length,
          last_page: Math.ceil(data.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, data.length),
          has_more_pages: page < Math.ceil(data.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        setCscStaffs(data);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (contractId) {
      fetchCscStaffs();
    }
  }, [contractId, page, perPage]);

  return { cscStaffs, pagination, loading, error };
};

export const useOutstandingRap = (id, startDate, endDate, page = 1, perPage = 10) => {
  const [formattedOutstandingRap, setFormattedOutstandingRap] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOutstandingRap = async () => {
      try {
        setLoading(true);
        const response = await API.getOutstandRap(id, startDate, endDate, page, perPage);

        if (response.success && response.rap) {
          const formatted = response.rap.map((res) => ({
            package_id: res.package_id,
            subproject: res.subproject,
            total_outstanding_RAP: res.total_outstanding_RAP,
            is_additional_paps: res.is_additional_paps,
            additional_paps: res.additional_paps,
            RAP_Status: res.RAP_Status,
            remarks: res.remarks,
          }));

          setFormattedOutstandingRap(formatted);
          setPagination(response.pagination);
        } else {
          // Handle legacy response format or empty data
          const rapData = response.rap || [];
          const formatted = rapData.map((res) => ({
            package_id: res.package_id,
            subproject: res.subproject,
            total_outstanding_RAP: res.total_outstanding_RAP,
            is_additional_paps: res.is_additional_paps,
            additional_paps: res.additional_paps,
            RAP_Status: res.RAP_Status,
            remarks: res.remarks,
          }));

          setFormattedOutstandingRap(formatted);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOutstandingRap();
  }, [id, startDate, endDate, page, perPage]);

  return { formattedOutstandingRap, pagination, loading, error };
};

export const useContractDetails = (apiUrl) => {
  const [contractDetails, setContractDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContractDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        const data = response.data.contract;
        const formattedContractDetails = {
          id: data.id,
          contractorId: data.contractor_id,
          name: data.name,
          contractNo: data.contract_no,
          originalContractSum: JSON.parse(data.original_contract_sum.amount),
          advanceRecoveryAmountTzs: JSON.parse(
            data.advance_recovery_amount_tzs
          ),
          deductionOfRetentionAmount: JSON.parse(
            data.deduction_of_retention_amount
          ),
          dateContractAgreementSigned: new Date(
            data.date_contract_agreement_signed
          ),
          dateOfCommencementOfWorks: new Date(
            data.date_of_commencement_of_works
          ),
          dateOfContractCompletion: new Date(data.date_of_contract_completion),
          defectsLiabilityNotificationPeriod: new Date(
            data.defects_liability_notification_period
          ),
          originalContractPeriod: data.original_contract_period,
        };
        setContractDetails(formattedContractDetails);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchContractDetails();
  }, [apiUrl]);

  return { contractDetails, loading, error };
};

export const useSubProjects = (filters = {}, initialPage = 1, initialPerPage = 10) => {
  const [subProjects, setSubProjects] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentPerPage, setCurrentPerPage] = useState(initialPerPage);

  const fetchSubProjects = async (page = currentPage, perPage = currentPerPage) => {
    try {
      setLoading(true);
      const response = await API.getSubProjects(filters, page, perPage);

        if (response.success && response.data) {
          let subProjectsData = [];
          let paginationData = null;
          
          if (response.data && response.data.data) {
            subProjectsData = Array.isArray(response.data.data) ? response.data.data : [];
            paginationData = response.data.pagination;
          } else if (Array.isArray(response.data)) {
            subProjectsData = response.data;
            paginationData = response.pagination;
          }
          
          const formattedSubProjects = subProjectsData.map((subProject) => ({
            id: subProject?.id,
            name: subProject?.name,
            quantity: subProject?.quantity,
            progress: subProject?.progress?.map((p) => ({
              quantity: p.target_quantity,
              unitOfTarget: p.unit_of_target,
              georeferenced_photos: p?.georeferenced_photos,
              qualificationOfProgress: p.qualification_of_progress,
              progressPercentage: p.progress_towards_target,
              descriptionOfTarget: p.description_of_target,
              designReviewTaken: p.changes_taken,
              remarks: p.remarks,
              reportStartDate: p.report_start_date
                ? new Date(p.report_start_date).toLocaleDateString()
                : null,
              reportEndDate: p.report_end_date
                ? new Date(p.report_end_date).toLocaleDateString()
                : null,
              latitude: p.latitude,
              longitude: p.longitude,
            })),
            images: subProject?.progress?.[0]?.georeferenced_photos
              ?.map((p) => ({ url: p.image_url, caption: p.description }))
              ?.filter((image) => image.url !== null && image.url !== undefined) || [],
        }));
        setSubProjects(formattedSubProjects);
        setPagination(paginationData);
        setCurrentPage(page);
        setCurrentPerPage(perPage);
      } else {
        const legacyData = response.data || response || [];
        const legacySubProjectsData = Array.isArray(legacyData) ? legacyData : [];
        const formattedSubProjects = legacySubProjectsData.map((subProject) => ({
          id: subProject?.id,
          name: subProject?.name,
          quantity: subProject?.quantity,
          progress: subProject?.progress?.map((p) => ({
            quantity: p.target_quantity,
            unitOfTarget: p.unit_of_target,
            georeferenced_photos: p?.georeferenced_photos,
            qualificationOfProgress: p.qualification_of_progress,
            progressPercentage: p.progress_towards_target,
            descriptionOfTarget: p.description_of_target,
            designReviewTaken: p.changes_taken,
            remarks: p.remarks,
            reportStartDate: p.report_start_date
              ? new Date(p.report_start_date).toLocaleDateString()
              : null,
            reportEndDate: p.report_end_date
              ? new Date(p.report_end_date).toLocaleDateString()
              : null,
            latitude: p.latitude,
            longitude: p.longitude,
          })),
          images: subProject?.progress?.[0]?.georeferenced_photos
            .map((p) => ({ url: p.image_url, caption: p.description }))
            .filter((image) => image.url !== null && image.url !== undefined),
        }));
        setSubProjects(formattedSubProjects);
        setPagination(null);
      }
    } catch (err) {
      setError(err);
      console.error("Failed to fetch sub-project data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubProjects(currentPage, currentPerPage);
  }, [JSON.stringify(filters)]);

  return { subProjects, pagination, loading, error, fetchSubProjects, currentPage, currentPerPage };
};

export const useSubProject = (id) => {
  const [subProjects, setSubProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubProjects = async () => {
      try {
        setLoading(true);
        const response = await API.getSubProject(id);


        // const formattedSubProjects = response.data.map((subProject) => ({
        //   id: subProject.id,
        //   name: subProject.name,
        //   progress: subProject.progress.map((p) => ({
        //     unitOfTarget: p.unit_of_target,
        //     qualificationOfProgress: p.qualification_of_progress,
        //     reportStartDate: p.report_start_date
        //       ? new Date(p.report_start_date).toLocaleDateString()
        //       : null,
        //     reportEndDate: p.report_end_date
        //       ? new Date(p.report_end_date).toLocaleDateString()
        //       : null,
        //     latitude: p.latitude,
        //     longitude: p.longitude,
        //   })),
        // }));

        setSubProjects(response);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch sub-project data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubProjects();
  }, [id]);

  return { subProjects, loading, error };
};

export const useQualityControls = (packageId, startDate, endDate, page = 1, perPage = 10) => {
  const [qualityControls, setQualityControls] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubProjectQualityControls = async () => {
      try {
        setLoading(true);
        const response = await API.getSubProjectQualityControls(
          packageId,
          startDate,
          endDate,
          page,
          perPage
        );

        if (response.success && response.data) {
          setQualityControls(response.data);
          setPagination(response.pagination);
        } else {
          // Handle legacy response format
          const legacyData = response.data || response || [];
          setQualityControls(legacyData);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch quality controls data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubProjectQualityControls();
  }, [packageId, startDate, endDate, page, perPage]);

  return { qualityControls, pagination, loading, error };
};
export const useGeneralRecommendations = (reportId) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGeneralRecommendations = async () => {
      try {
        setLoading(true);
        // const { data } = await API.getGeneralRecommendations(
        //   procuringEntityPackageId
        // );
        const { data } = await API.getGeneralRecommendationsByReport(
          reportId
        );

        setRecommendations(data.data.data);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch recommendations data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGeneralRecommendations();
  }, [reportId]);

  return { recommendations, loading, error };
};

export const useTrpImplementation = (packageId, startDate, endDate, page = 1, perPage = 10) => {
  const [trpImplementations, setTrpImplementations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrpImplementations = async () => {
      try {
        setLoading(true);
        const response = await API.getTrpImplementations(packageId, page, perPage, startDate, endDate);

        if (response.success && response.data && response.data.data) {
          const formattedData = response.data.data.map((item) => ({
            ...item,
            id: item.id,
            sub_project: item.sub_project?.name || "-",
            sub_project_type: item.sub_project_type?.name || "-",
            vendors_by_gender: item.vendors_by_gender
              ?.map((vendorGender) => `${vendorGender.gender}(${vendorGender.count})`)
              .join(", ") || "-",
            disabled_vendors:
              item.vulnerabilities?.find((vuln) =>
                vuln.vulnerability.toLowerCase().includes("disabled")
              )?.count || 0,
            elders:
              item.vulnerabilities?.find((vuln) =>
                vuln.vulnerability.toLowerCase().includes("elders")
              )?.count || 0,
            food_vendors:
              item.vendor_categories?.find((cat) =>
                cat.category.toLowerCase().includes("food vendors")
              )?.count || 0,
            vegetable_and_fruits:
              item.vendor_categories?.find((cat) =>
                cat.category.toLowerCase().includes("vegetable and fruits")
              )?.count || 0,
          }));
          
          setTrpImplementations(formattedData);
          setPagination(response.pagination);
        } else {
          // Handle legacy response format
          const legacyData = response.data?.data || response.data || [];
          const formattedData = legacyData.map((item) => ({
            ...item,
            id: item.id,
            sub_project: item.sub_project?.name || "-",
            sub_project_type: item.sub_project_type?.name || "-",
            vendors_by_gender: item.vendors_by_gender
              ?.map((vendorGender) => `${vendorGender.gender}(${vendorGender.count})`)
              .join(", ") || "-",
            disabled_vendors:
              item.vulnerabilities?.find((vuln) =>
                vuln.vulnerability.toLowerCase().includes("disabled")
              )?.count || 0,
            elders:
              item.vulnerabilities?.find((vuln) =>
                vuln.vulnerability.toLowerCase().includes("elders")
              )?.count || 0,
            food_vendors:
              item.vendor_categories?.find((cat) =>
                cat.category.toLowerCase().includes("food vendors")
              )?.count || 0,
            vegetable_and_fruits:
              item.vendor_categories?.find((cat) =>
                cat.category.toLowerCase().includes("vegetable and fruits")
              )?.count || 0,
          }));
          
          setTrpImplementations(formattedData);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch trp implementations data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrpImplementations();
  }, [packageId, startDate, endDate, page, perPage]);

  return { trpImplementations, pagination, loading, error };
};


export const useSubProjectPCTChallenges = (subProjectId, startDate = null, endDate = null) => {
  const [pctChallenges, setPCTChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPCTChallenges = async () => {
      if (!subProjectId) {
        setLoading(false);
        return;
      }
      const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).replace(/ /g, ' ');
      };
      
      try {
        setLoading(true);
        const response = await API.getPCTChallenges(subProjectId, startDate, endDate);

        if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
          const formattedData = response.data.data.data.map((item) => ({
            ...item,
            id: item.id,
            pct_report_id: item.pct_report_id,
            challenge_description: item.challenge_description,
            action_taken: item.action_taken,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            email: item.user?.email,
            staff_name: item.pct_report?.staff_name,
            section: item.pct_report?.section,
            way_forward: item.way_forward,
            visit_date: formatDate(item?.pct_report?.visit_date),
            exit_date: formatDate(item?.pct_report?.exit_date),
          }));
          setPCTChallenges(formattedData);
        } else {
          setPCTChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pct challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPCTChallenges();
  }, [subProjectId, startDate, endDate]);

  return { pctChallenges, loading, error };
};

export const useSubProjectPITChallenges = (subProjectId, startDate = null, endDate = null) => {
  const [pitChallenges, setPITChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPITChallenges = async () => {
      if (!subProjectId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await API.getPITChallenges(subProjectId, startDate, endDate);

        const formatDate = (date) => {
          return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }).replace(/ /g, ' ');
        };

        if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
          const formattedData = response.data.data.data.map((item) => ({
            id: item.id,
            pit_report_id: item.pit_report_id,
            challenge: item.challenge,
            action_taken: item.action_taken,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            created_at: item.created_at,
            updated_at: item.updated_at,
            pit_report: item.pit_report,
            procuring_entity_package: item.procuring_entity_package,
            sub_project: item.sub_project,
            staff_name: item.pit_report?.staff_name,
            section: item.pit_report?.section,
            visit_date: formatDate(item.pit_report?.visit_date),
          }));  
          setPITChallenges(formattedData);
        } else {
          setPITChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pit challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPITChallenges();
  }, [subProjectId, startDate, endDate]);

  return { pitChallenges, loading, error };
};

export const useSubProjectCSCChallenges = (subProjectId, startDate = null, endDate = null) => {
  const [cscChallenges, setCSCChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCSCChallenges = async () => {
      if (!subProjectId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await API.getCSCChallenges(subProjectId, startDate, endDate);

        if (response?.data?.data && Array.isArray(response.data.data)) {
          const formattedData = response.data.data.map((item) => ({
            id: item.id,
            package_id: item.package_id,
            sub_project_id: item.sub_project_id,
            works_category: item.works_category,
            challenge: item.challenge,
            way_forward: item.way_forward,
            created_at: item.created_at,
            updated_at: item.updated_at,
            report_start_date: item.report_start_date,
            report_end_date: item.report_end_date,
            package: item.package,
            sub_project: item.sub_project,
            visit_date: isoDateToHumanReadableDate(item.report_start_date, "DD MMM YYYY"),
            exit_date: isoDateToHumanReadableDate(item.report_end_date, "DD MMM YYYY"),
          }));
          setCSCChallenges(formattedData);
        } else {
          setCSCChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch csc challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCSCChallenges();
  }, [subProjectId, startDate, endDate]);

  return { cscChallenges, loading, error };
};

export const useSubProjectChallenges = (subProjectId) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        setLoading(true);
        const { data } = await API.getChallenges(subProjectId);

        const formattedData = data.data.filter((item) => Number(item.sub_project_id) === Number(subProjectId)).map((item) => ({
          ...item,
          "package_id": item.package_id,
          "sub_project_id": item.sub_project_id,
          "works_category": item.works_category,
          "challenge": item.challenge,
          "way_forward": item.way_forward,
          "report_start_date": item.report_start_date,
          "report_end_date": item.report_end_date,
          "sub_project": item.sub_project,
          "package": item.package
        }));
        setChallenges(formattedData);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pct challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, [subProjectId]);

  return { challenges, loading, error };
};

export const usePCTReport = (procuringEntityId, startDate = null, endDate = null, page = 1, perPage = 15) => {
  const [pctReport, setPCTReport] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPCTReport = async () => {
      try {
        setLoading(true);
        const { data:procuringEntity } = await API.getProcuringEntity(procuringEntityId);
        const response = await API.getPCTReports(procuringEntity.council_id, startDate, endDate, page, perPage);


        // Handle exact data format: { data: [], pagination: {} }
        // axios response structure: response.data = { data: [], pagination: {} }
        const reportData = Array.isArray(response.data.data) ? response.data.data : [];
        const paginationData = response.data.pagination || {
          current_page: page,
          per_page: perPage,
          total: reportData.length,
          last_page: Math.ceil(reportData.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, reportData.length),
          has_more_pages: page < Math.ceil(reportData.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        console.log('PCT Report Data:', reportData);
        console.log('PCT Pagination Data:', paginationData);

        setPCTReport(reportData);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pct report data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPCTReport();
    }, [procuringEntityId, startDate, endDate, page, perPage]);

  return { pctReport, pagination, loading, error };
};


export const usePMCReport = (procuringEntityId, startDate = null, endDate = null, page = 1, perPage = 15) => {
  const [pmcReport, setPMCReport] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPMCReport = async () => {
      try {
        setLoading(true);
        const { data:procuringEntity } = await API.getProcuringEntity(procuringEntityId);
        const response = await reportsAPI.getPMCReports(procuringEntity.council_id, startDate, endDate, page, perPage);

        console.log('PMC API Response:', response);
        console.log('PMC Response Data:', response.data);

        // Handle exact data format: { data: [], pagination: {} }
        // axios response structure: response.data = { data: [], pagination: {} }
        const reportData = Array.isArray(response.data.data) ? response.data.data : [];
        const paginationData = response.data.pagination || {
          current_page: page,
          per_page: perPage,
          total: reportData.length,
          last_page: Math.ceil(reportData.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, reportData.length),
          has_more_pages: page < Math.ceil(reportData.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        console.log('PMC Report Data:', reportData);
        console.log('PMC Pagination Data:', paginationData);

        setPMCReport(reportData);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pmc report data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPMCReport();
    }, [procuringEntityId, startDate, endDate, page, perPage]);

  return { pmcReport, pagination, loading, error };
};

export const usePITReport = (procuringEntityId, startDate = null, endDate = null, page = 1, perPage = 15) => {
  const [pitReport, setPITReport] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
    from: 1,
    to: 0,
    has_more_pages: false,
    next_page_url: null,
    prev_page_url: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPITReport = async () => {
      try {
        setLoading(true);
        const { data:procuringEntity } = await API.getProcuringEntity(procuringEntityId);
        const response = await API.getPITReports(procuringEntity.council_id, startDate, endDate, page, perPage);

        console.log('PIT API Response:', response);
        console.log('PIT Response Data:', response.data);

        // Handle exact data format: { data: [], pagination: {} }
        // axios response structure: response.data = { data: [], pagination: {} }
        const reportData = Array.isArray(response.data.data) ? response.data.data : [];
        const paginationData = response.data.pagination || {
          current_page: page,
          per_page: perPage,
          total: reportData.length,
          last_page: Math.ceil(reportData.length / perPage),
          from: (page - 1) * perPage + 1,
          to: Math.min(page * perPage, reportData.length),
          has_more_pages: page < Math.ceil(reportData.length / perPage),
          next_page_url: null,
          prev_page_url: null
        };

        console.log('PIT Report Data:', reportData);
        console.log('PIT Pagination Data:', paginationData);

        setPITReport(reportData);
        setPagination(paginationData);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch pit report data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPITReport();
  }, [procuringEntityId, startDate, endDate, page, perPage]);

  return { pitReport, pagination, loading, error };
};

// Hook for fetching projects with pagination
export const useProjects = (page = 1, perPage = 10, filters = {}) => {
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await API.getProjects(filters, page, perPage);
        
        if (response.success && response.data) {
          setProjects(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setProjects(response.data || []);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch projects data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [page, perPage, JSON.stringify(filters)]);

  return { projects, pagination, loading, error };
};

// Hook for fetching project regions with pagination
export const useProjectRegions = (projectId, page = 1, perPage = 10) => {
  const [regions, setRegions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetchRegions = async () => {
      try {
        setLoading(true);
        const response = await API.getProjectRegions(projectId, page, perPage);
        
        if (response?.success && response?.data) {
          const regionsPayload = response.data;
          const regionsData = Array.isArray(regionsPayload?.data)
            ? regionsPayload.data
            : Array.isArray(regionsPayload)
            ? regionsPayload
            : [];
          setRegions(regionsData);
          setPagination(regionsPayload?.pagination ?? null);
        } else {
          const legacyPayload = response?.data ?? response ?? [];
          const regionsData = Array.isArray(legacyPayload) ? legacyPayload : [];
          setRegions(regionsData);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch project regions data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRegions();
  }, [projectId, page, perPage]);

  return { regions, pagination, loading, error };
};

// Hook for fetching project councils with pagination
export const useProjectCouncils = (projectId, page = 1, perPage = 10) => {
  const [councils, setCouncils] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetchCouncils = async () => {
      try {
        setLoading(true);
        const response = await API.getProjectCouncils(projectId, page, perPage);
        
        if (response.success && response.data) {
          setCouncils(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setCouncils(response.data || []);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch project councils data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCouncils();
  }, [projectId, page, perPage]);

  return { councils, pagination, loading, error };
};


export const useProjectCouncilStats = (projectId, page = 1, per_page = 10, filters = {}, refreshKey = 0) => {
  const [councilStats, setCouncilStats] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    report_start_date,
    report_end_date,
    submission_status,
    approval_status,
    processing_status
  } = filters || {};

  useEffect(() => {

    if(!projectId) {
      setLoading(false);
      return;
    }

    const fetchCouncilStats = async () => {
      try {
  
        setLoading(true);
        const response = await API.getProjectCouncilStats(projectId, page, per_page, {
          report_start_date,
          report_end_date,
          submission_status,
          approval_status,
          processing_status
        });
  
        if (response.data) {
          setCouncilStats(response.data);
          setPagination(response.pagination);
        } 
      } catch (err) {
         setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCouncilStats();

  }, [projectId, page, per_page, report_start_date, report_end_date, submission_status, approval_status, processing_status, refreshKey]);

  return { councilStats, pagination, loading, error }

}


export const useAlertTemplate = (projectId, councilId, period = {}, enabled = true) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { report_start_date, report_end_date } = period || {};

  useEffect(() => {

    if (!projectId || !councilId || !enabled) {
      return;
    }

    let active = true;
    setTemplate(null);

    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await API.getAlertTemplate({
          project_id: projectId,
          council_id: councilId,
          report_start_date,
          report_end_date
        });

        if (active) {
          setTemplate(response.data);
        }
      } catch (err) {
        if (active) {
          setError(err);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchTemplate();

    return () => { active = false; };

  }, [projectId, councilId, enabled, report_start_date, report_end_date]);

  return { template, loading, error }

}


// Hook for fetching packages with pagination
export const usePackages = (filters = {}, page = 1, perPage = 10) => {
  const [packages, setPackages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await API.getProcuringEntityPackages(filters, page, perPage);
        
        if (response.success && response.data) {
          setPackages(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setPackages(response.data || []);
          setPagination(null);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch packages data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [page, perPage, JSON.stringify(filters)]);

  return { packages, pagination, loading, error };
};

export const usePackagePCTChallenges = (packageId = null, startDate = null, endDate = null, councilId = undefined, isProcuringEntityLoading = false) => {
  const [pctChallenges, setPctChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPctChallenges = async () => {
      if (isProcuringEntityLoading && packageId !== null) {
        setLoading(true);
        setPctChallenges([]);
        return;
      }

      if (!packageId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await API.getPCTChallenges(null, startDate, endDate, councilId);

        if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
          const formattedData = response.data.data.data.map((item) => ({
            ...item,
            id: item.id,
            pct_report_id: item.pct_report_id,
            challenge: item.challenge,
            action_taken: item.action_taken,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            email: item.user?.email,
            staff_name: item.pct_report?.staff_name,
            section: item.pct_report?.section,
            way_forward: item.way_forward,
            sub_project: item?.pct_report?.sub_projects_visited && item?.pct_report?.sub_projects_visited.length > 0 
              ? item?.pct_report?.sub_projects_visited.map(sp => sp?.sub_project?.name).join(', ')
              : 'N/A',
            visit_date: item.pct_report?.visit_date,
            exit_date: item.pct_report?.exit_date,
          }));
          setPctChallenges(formattedData);
        } else {
          console.log("Package PCT Challenges data is not an array or is empty:", response?.data?.data);
          setPctChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch package PCT challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPctChallenges();
  }, [packageId, startDate, endDate, councilId, isProcuringEntityLoading]);

  return { pctChallenges, loading, error };
};

export const usePackagePITChallenges = (packageId = null, startDate = null, endDate = null, councilId = undefined, isProcuringEntityLoading = false) => {
  const [pitChallenges, setPitChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPitChallenges = async () => {
      if (isProcuringEntityLoading && packageId !== null) {
        setLoading(true);
        setPitChallenges([]);
        return;
      }

      if (!packageId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await API.getPITChallenges(null, startDate, endDate, councilId);

        if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
          const formattedData = response.data.data.data.map((item) => ({
            ...item,
            id: item.id,
            pit_report_id: item.pit_report_id,
            challenge: item.challenge,
            action_taken: item.action_taken,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            email: item.user?.email,
            staff_name: item.pit_report?.staff_name,
            section: item.pit_report?.section,
            way_forward: item.way_forward,
            sub_project: item.sub_project?.name || 'N/A',
            visit_date: item.pit_report?.visit_date,
          }));
          setPitChallenges(formattedData);
        } else {
          console.log("Package PIT Challenges data is not an array or is empty:", response?.data?.data?.data);
          setPitChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch package PIT challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPitChallenges();
  }, [packageId, startDate, endDate, councilId, isProcuringEntityLoading]);

  return { pitChallenges, loading, error };
};

export const usePackageCSCChallenges = (packageId = null, startDate = null, endDate = null, councilId = undefined, isProcuringEntityLoading = false) => {
  const [cscChallenges, setCscChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCscChallenges = async () => {
      if (isProcuringEntityLoading && packageId !== null) {
        setLoading(true);
        setCscChallenges([]);
        return;
      }

      if (!packageId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await API.getCSCChallenges(null, startDate, endDate, councilId);

        if (response?.data?.data && Array.isArray(response.data.data)) {
          const formattedData = response.data.data.map((item) => ({
            ...item,
            id: item.id,
            challenge: item.challenge,
            way_forward: item.way_forward,
            photo_url: item.photo_url,
            latitude: item.latitude,
            longitude: item.longitude,
            email: item.user?.email,
            staff_name: item.staff_name,
            works_category: item.works_category,
            sub_project: item.sub_project?.name || 'N/A',
            report_start_date: item.report_start_date,
            report_end_date: item.report_end_date,
          }));
          setCscChallenges(formattedData);
        } else {
          console.log("Package CSC Challenges data is not an array or is empty:", response?.data?.data);
          setCscChallenges([]);
        }
      } catch (err) {
        setError(err);
        console.error("Failed to fetch package CSC challenges data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCscChallenges();
  }, [packageId, startDate, endDate, councilId, isProcuringEntityLoading]);

  return { cscChallenges, loading, error };
};
